import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';

@Injectable({
  providedIn: 'root',
})
export class WhatsappThemeService {
  private readonly storageKey = 'chatlink_whatsapp_dark_mode';
  private readonly cookieMaxAgeSeconds = 60 * 60 * 24 * 365 * 5;
  private readonly darkModeState = signal<boolean>(this.loadStoredPreference());
  private readonly document = inject(DOCUMENT);
  private readonly overlayContainer = inject(OverlayContainer);

  readonly isDarkMode = this.darkModeState.asReadonly();

  constructor() {
    effect(() => this.syncThemeClass(this.darkModeState()));
  }

  toggleDarkMode(): void {
    this.setDarkMode(!this.darkModeState());
  }

  setDarkMode(enabled: boolean): void {
    this.darkModeState.set(enabled);
    document.cookie = `${this.storageKey}=${String(enabled)}; max-age=${this.cookieMaxAgeSeconds}; path=/; SameSite=Lax`;
  }

  private loadStoredPreference(): boolean {
    const cookieValue = this.getCookie(this.storageKey);
    if (cookieValue !== null) {
      return cookieValue === 'true';
    }

    const legacyValue = localStorage.getItem(this.storageKey);
    if (legacyValue === 'true' || legacyValue === 'false') {
      const enabled = legacyValue === 'true';
      document.cookie = `${this.storageKey}=${String(enabled)}; max-age=${this.cookieMaxAgeSeconds}; path=/; SameSite=Lax`;
      localStorage.removeItem(this.storageKey);
      return enabled;
    }

    return false;
  }

  private getCookie(name: string): string | null {
    const prefix = `${name}=`;
    const cookie = document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith(prefix));

    return cookie ? decodeURIComponent(cookie.substring(prefix.length)) : null;
  }

  private syncThemeClass(enabled: boolean): void {
    const className = 'app-theme--dark';
    this.document.documentElement.classList.toggle(className, enabled);
    this.document.body.classList.toggle(className, enabled);
    this.overlayContainer.getContainerElement().classList.toggle(className, enabled);
  }
}
